'use client'

import ButtonLink from '@/design-system/buttons/ButtonLink'
import { useSimulateurPage } from '@/hooks/navigation/useSimulateurPage'
import { useClientTranslation } from '@/hooks/useClientTranslation'
import { useCurrentSimulation } from '@/publicodes-state'
import { trackEvent } from '@/utils/analytics/trackEvent'
import { useEffect, useState, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import RestartIcon from '../icons/RestartIcon'
import Trans from '../translation/trans/TransClient'
import { usePathname } from 'next/navigation'
import { useGoFusionToken } from '@/hooks/useGoFusionToken'
export default function DynamicCTAButtons({
  className,
  trackingEvents,
  withRestart = true,
}: {
  className?: string
  trackingEvents: {
    start: string[]
    resume: string[]
    results: string[]
    restart?: string[]
  }
  withRestart?: boolean
}) {
  const pathname = usePathname()
  const { token, redirectUrl, hasTakenTest } = useGoFusionToken()
  const { t } = useClientTranslation()
  const {
    getLinkToSimulateurPage,
    goToSimulateurPage,
    linkToSimulateurPageLabel,
  } = useSimulateurPage()

  const { progression } = useCurrentSimulation()

  const [isHover, setIsHover] = useState(false)

  const [isClient, setIsClient] = useState(false)

  // Backend says this user has not taken the test — ignore stale local state
  const effectivelyNewUser = hasTakenTest === false

  const mainButtonLabel = effectivelyNewUser ? t('Passer le test') : linkToSimulateurPageLabel
  const mainButtonHref = effectivelyNewUser
    ? getLinkToSimulateurPage({ newSimulation: true })
    : getLinkToSimulateurPage()

  const skipButtonLabel = useMemo(() => {
    if (effectivelyNewUser || progression === 0) {
      return 'Ne pas faire le test'
    }
    return 'Passer le test'
  }, [progression, effectivelyNewUser])

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient) {
    return null
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex flex-col flex-wrap items-center justify-center gap-2 md:items-start lg:flex-row lg:flex-nowrap">
        <ButtonLink
          size="xl"
          className={twMerge(
            'hover:bg-primary-900 transition-all duration-300 take-test-button',
            className
          )}
          href={mainButtonHref}
          data-cypress-id="do-the-test-link"
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          onClick={() => {
            if (effectivelyNewUser) {
              trackEvent(trackingEvents?.start)
              return
            }

            if (progression === 1) {
              trackEvent(trackingEvents?.results)
              return
            }

            if (progression > 0) {
              trackEvent(trackingEvents?.resume)
              return
            }

            trackEvent(trackingEvents?.start)
          }}>
          <span
            className={twMerge(
              isHover
                ? 'bg-rainbow animate-rainbow-fast bg-clip-text! text-transparent! duration-1000'
                : '',
              'leading-none'
            )}>
            <Trans>{mainButtonLabel}</Trans>
          </span>
        </ButtonLink>

        {withRestart && !effectivelyNewUser && progression > 0 && (
          <ButtonLink
            size="xl"
            color="secondary"
            className="leading-none green-text-border"
            trackingEvent={trackingEvents?.restart}
            onClick={() => {
              goToSimulateurPage({ noNavigation: true, newSimulation: {} })
            }}
            href={getLinkToSimulateurPage({ newSimulation: true })}>
            <RestartIcon className="fill-secondary-700 mr-2" />

            <Trans>Recommencer</Trans>
          </ButtonLink>
        )}
      </div>
      <ButtonLink
        size="xl"
        color="text"
        className="leading-none close-skip-button"
        href={'#'}
        onClick={() => {
          if (token) {
            window.location.href = `${redirectUrl}/dashboard?token=${token}`;
          } else {
            window.history.pushState(null, '', `${pathname}?skip`);
          }
        }}>
        <Trans>{skipButtonLabel}</Trans>
      </ButtonLink>
    </div>
  )
}
